// ── خطوط المتجر (عربي / إنجليزي) ─────────────────────────────────────────────
// التاجر يختار خطّين من إعدادات المتجر: خط للعربي وخط للإنجليزي — أي خط من
// Google Fonts بكتابة اسمه. يُطبَّقان على *كل* المتجر العام عبر متغيّري CSS:
//
//   --stf     : الخط العام (كل نصوص المتجر). ترتيب المكدّس حسب لغة الزائر:
//               عربي → [العربي, الإنجليزي, sans-serif] / إنجليزي → العكس.
//               المتصفّح يختار الخط لكل حرف: الحروف اللاتينية تأخذ الخط الأول
//               الذي يملك رسمها، فيبقى العرض متناسقاً في اللغتين.
//   --stf-en  : خط العرض الإنجليزي (اسم المتجر اللاتيني تحديداً).
//
// الأداء (قاعدة «الحفاظ على سرعة الموقع»):
//   • الخطوط الافتراضية (Cairo + Playfair Display) محمّلة أصلاً في index.html —
//     فالوضع الافتراضي لا يضيف *أي* طلب شبكة ولا بايت واحد.
//   • أي خط إضافي يُحمَّل بوسم <link media="print"> ثم يُفعَّل عند onload، أي أنه
//     لا يحجب رسم الصفحة إطلاقاً (non-render-blocking) + display=swap فالنص يظهر
//     فوراً بخط احتياطي ثم يُبدَّل.
//   • الاختيار يُخزَّن محلياً (مثل تفضيلات شاشة التحميل) فيُطبَّق في الزيارة
//     التالية قبل وصول إعدادات Firestore — بلا وميض تبديل خط.

export const STORE_FONT_AR_DEFAULT = "Cairo";
export const STORE_FONT_EN_DEFAULT = "Playfair Display";

// محمّلة مسبقاً في public/index.html — اختيارها بلا أي تكلفة إضافية.
// ⚠️ Playfair Display أُخرج من هنا عمداً مع إخراجه من الوسم الحاجب: بقاؤه في
// هذه القائمة كان سيعني أن loadGoogleFont يتخطّاه فلا يُحمَّل إطلاقاً بعد حذفه
// من index.html — أي خطّ لاتيني مفقود لا خطّ مؤجَّل.
const PRELOADED = ["Cairo"];

// قوائم الخطوط الجاهزة تعيش في ملف منفصل (storeFontPresets.js) تستورده لوحة
// الإدارة فقط — كي لا تدخل باندل المتجر/التطبيق الأساسي بلا فائدة.

// اسم خط آمن: حروف/أرقام/مسافات/شرطات فقط — يمنع أي حقن في قيمة CSS أو الرابط
export function sanitizeFontName(v) {
  return String(v || "").replace(/[^A-Za-z0-9 \-]/g, "").replace(/\s+/g, " ").trim().slice(0, 40);
}

const q = (n) => `'${n}'`;

// مكدّس الخطوط: الأساسي ثم الثانوي (لحروف لا يملكها الأول) ثم خط النظام
export function fontStack(primary, secondary) {
  const list = [];
  if (primary) list.push(q(primary));
  if (secondary && secondary !== primary) list.push(q(secondary));
  list.push("sans-serif");
  return list.join(",");
}

// ── تحميل الخط من Google Fonts بلا حجب الرسم ────────────────────────────────
const _requested = new Set(PRELOADED);

const googleHref = (name, spec) =>
  `https://fonts.googleapis.com/css2?family=${name.replace(/ /g, "+")}${spec ? ":" + spec : ""}&display=swap`;

export function loadGoogleFont(name) {
  const n = sanitizeFontName(name);
  if (!n || _requested.has(n) || typeof document === "undefined") return;
  _requested.add(n);
  // بعض الخطوط لا تملك كل الأوزان — Google يرد 400 على الطلب كاملاً، فنتدرّج:
  // كل الأوزان → أساسي/عريض → الخط بلا تحديد وزن.
  const specs = ["wght@400;500;600;700;800;900", "wght@400;700", ""];
  let i = 0;
  const attach = () => {
    if (i >= specs.length) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = googleHref(n, specs[i++]);
    link.media = "print";                       // لا يحجب أول رسم للصفحة
    link.onload = () => { link.media = "all"; }; // يُفعَّل بعد وصوله
    link.onerror = () => { try { link.remove(); } catch {} attach(); };
    document.head.appendChild(link);
  };
  attach();
}

// ── تطبيق الخطوط على المتجر كله ─────────────────────────────────────────────
export function applyStoreFonts({ fontAr, fontEn, lang } = {}) {
  if (typeof document === "undefined") return;
  const ar = sanitizeFontName(fontAr) || STORE_FONT_AR_DEFAULT;
  const en = sanitizeFontName(fontEn) || STORE_FONT_EN_DEFAULT;
  const root = document.documentElement;
  root.style.setProperty("--stf", lang === "en" ? fontStack(en, ar) : fontStack(ar, en));
  root.style.setProperty("--stf-en", `${q(en)},'Georgia',serif`);
  loadGoogleFont(ar);
  loadGoogleFont(en);
}

// ── الكاش المحلي (يُقرأ قبل وصول الإعدادات من Firestore) ────────────────────
// المفتاح مربوط بالدومين + رقم الشركة (على دومين المنصّة) تماماً كتفضيلات اللودر.
export function storeFontsKey(cid) {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  return `bkv_fonts_${host}${cid ? "_" + cid : ""}`;
}

export function readStoreFonts(cid) {
  try {
    const p = JSON.parse(localStorage.getItem(storeFontsKey(cid)) || "null") || {};
    return {
      fontAr: sanitizeFontName(p.fontAr) || STORE_FONT_AR_DEFAULT,
      fontEn: sanitizeFontName(p.fontEn) || STORE_FONT_EN_DEFAULT,
    };
  } catch {
    return { fontAr: STORE_FONT_AR_DEFAULT, fontEn: STORE_FONT_EN_DEFAULT };
  }
}

export function saveStoreFonts(cid, { fontAr, fontEn }) {
  try {
    localStorage.setItem(storeFontsKey(cid), JSON.stringify({
      fontAr: sanitizeFontName(fontAr) || STORE_FONT_AR_DEFAULT,
      fontEn: sanitizeFontName(fontEn) || STORE_FONT_EN_DEFAULT,
    }));
  } catch {}
}

// يُستدعى في أول لحظة من سياق المتجر (قبل تحميل الإعدادات) — من الكاش
export function applyCachedStoreFonts(cid) {
  let lang = "ar";
  try { lang = localStorage.getItem("bkv_lang") || "ar"; } catch {}
  applyStoreFonts({ ...readStoreFonts(cid), lang });
}
